import { useAtomValue } from "jotai";
import styles from "./styles.module.scss";
import { getUserGrade, userData, userProfile } from "../../state";
import { Link } from "react-router-dom";
import avatar from "../../assets/user.png";
import { ReactComponent as Tree } from "../../assets/chart-tree.svg";
import { ReactComponent as Search } from "../../assets/search-alt.svg";
import { ReactComponent as Message } from "../../assets/message.svg";
import { ReactComponent as CheckBox } from "../../assets/checkbox.svg";
import { ReactComponent as VerifiedBadge } from "../../assets/verified-badge.svg";
import { hasDateBeenReached } from "../../utils/formatDate";
import { downloadMembershipBadge, MemberType } from "../../requests";
import { formatFullName, truncateString } from "../../utils/stringHelpers";
import { App, Button, Dropdown, Tooltip } from "antd";
import type { MenuProps } from "antd";
import { useMutation } from "@tanstack/react-query";
import { errorMessage } from "../../utils/errorMessage";
import { useEffect, useState } from "react";

const ProfileCard = ({
  addAboutLink,
  showLicenseScore,
  showUpgradeScore,
  isFinanciallyActive,
}: {
  addAboutLink?: boolean;
  showLicenseScore?: boolean;
  showUpgradeScore?: boolean;
  isFinanciallyActive?: boolean;
}) => {
  const { message } = App.useApp();
  const profile = useAtomValue(userProfile);
  const user = useAtomValue(userData);
  const [isDownloading, setIsDownloading] = useState(false);
  const grade = getUserGrade(user?.MembershipTypeId!);
  const hasExpiredLicense = hasDateBeenReached(profile?.LicenseExpire!);
  const isCorporate = user?.MembershipTypeId === MemberType.Corporate;
  const fullName = formatFullName(
    profile?.LastName,
    profile?.FirstName,
    profile?.MiddleName,
  );

  const downloadBadgeMutation = useMutation({
    mutationFn: downloadMembershipBadge,
  });

  useEffect(() => {
    if (downloadBadgeMutation.isPending || isDownloading) {
      message.loading("Downloading membership badge...");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadBadgeMutation.isPending, isDownloading]);
  const handleDownloadCertificate = async (memberId: string) => {
    try {
      await downloadBadgeMutation.mutateAsync(memberId, {
        onSuccess: (data) => {
          setIsDownloading(true);
          const url = window.URL.createObjectURL(data);
          const a = document.createElement("a");
          a.href = url;
          a.download = `Membership_Badge_${profile?.MembershipNo}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          setIsDownloading(false);
        },
      });
    } catch (error: any) {
      message.destroy();
      let response = await error?.response?.data.text();
      const actualObj = JSON.parse(response);
      message.error(actualObj?.Message || errorMessage(error));
    }
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "status",
      label: "Financially Active",
      disabled: true,
      style: { fontWeight: 600, color: "#000" },
    },
    {
      type: "divider",
    },
    {
      key: "download",
      label: "Download Membership Badge",
      onClick: () => handleDownloadCertificate(profile?.MembershipNo!),
    },
  ];

  return (
    <div className={styles.largeGap}>
      <div className={styles.mediumGap}>
        <img src={profile?.UploadPictureUrl || avatar} alt="profile" />
        <div>
          <div className={styles.nameBadge}>
            {fullName?.length > 40 ? (
              <Tooltip title={fullName} placement="bottom">
                <h2>{truncateString(fullName, 40)}</h2>
              </Tooltip>
            ) : (
              <h2>{fullName}</h2>
            )}
            {isFinanciallyActive ? (
              <Dropdown menu={{ items: menuItems }} trigger={["hover"]}>
                <Button
                  type="text"
                  icon={<VerifiedBadge style={{ cursor: "pointer" }} />}
                />
              </Dropdown>
            ) : null}
          </div>
          {addAboutLink ? (
            <Link className={styles.extLink} to="/about-hrpl">
              About HRPL
            </Link>
          ) : null}
        </div>
      </div>
      <div className={styles.statsGroup}>
        <div className={styles.stat}>
          <div className={styles.icon1}>
            <Tree />
          </div>
          <section>
            <h4>{grade}</h4>
            <label>Grade</label>
          </section>
        </div>
        <div className={styles.stat}>
          <div className={styles.icon2}>
            <Search />
          </div>
          <section>
            <h4>{profile?.MembershipNo}</h4>
            <label>Number</label>
          </section>
        </div>
        <div className={styles.stat}>
          <div className={styles.icon3}>
            <Message />
          </div>
          {showLicenseScore ? (
            <section>
              <h4>{profile?.LicenseScore}</h4>
              <label>License Score</label>
            </section>
          ) : showUpgradeScore ? (
            <section>
              <h4>{profile?.UpgradeScore}</h4>
              <label>Upgrade Score</label>
            </section>
          ) : (
            <section>
              <h4>{profile?.LicenseScore + profile?.UpgradeScore}</h4>
              <label>Score</label>
            </section>
          )}
          {isCorporate ? null : (
            <div className={styles.stat}>
              <div className={styles.icon1}>
                <CheckBox />
              </div>
              <section>
                <h4>{hasExpiredLicense ? "Expired" : "Active"}</h4>
                <label>License Status</label>
              </section>
            </div>
          )}
          {/* <div className={styles.stat}>
            <div className={styles.icon1}>
              <CheckBox />
            </div>
            <section>
              <h4>{format(parseISO(profile?.LicenseExpire), "do MMM yyyy")}</h4>
              <label>License Expiry Date</label>
            </section>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;
